// The NEWS section of the hub (R229).
//
// What it reads live:
//   - `GET /api/news?limit=10` — the public list, newest first. Each item
//     carries its whole body, so every card shows its article text at once
//     and the panel has no READ or CLOSE control.
//
// **An article body is Markdown and it travels as data** (R154). The meta
// server never renders it, and this panel never renders it
// either: the body goes into the document as escaped text inside a block
// that keeps its line breaks. There is no Markdown parser in the client,
// and nothing a writer typed can become markup.
//
// What is honestly empty today:
//   - Nothing is published on a new deployment. The list is then empty, and
//     the panel says so rather than drawing an invented article.
//   - The community card carries one link and no figure. The mockup drew
//     "412 members, 38 online now"; no route answers either number, so this
//     panel prints neither.
//   - The mockup's THIS WEEK card counted a season down. That schedule
//     belongs to the PLAY section's `/api/modes` answer, so it is not
//     repeated here.
//
// The badge dot (ui-map-interface.md §16.4): `dot()` is on while the newest
// article is newer than the stamp this browser holds, and opening the
// section is what clears it. The dot and the list read ONE cache cell, so
// the shell's tick costs one `GET /api/news` per cell life and never a
// request per redraw. A render of the ready list writes the stamp one time
// per visit (`hubstate.ts`).

import { DISCORD_URL } from '../../../gen/balance';
import { getNews, type NewsItem, type NewsPage } from '../../../net/api';
// The page size rides presentation.toml [presentation.chrome].
import { CHROME_NEWS_PAGE } from '../../../gen/presentation';
import {
  NEWS_SEEN_KEY, TTL_ON_OPEN, formatAgo, loadSeenAt, newestNewsAt, newsAt, newsDot, saveSeenAt, type Cell,
} from '../hubstate';
import { hubNews as S } from '../../../gen/strings';
import {
  card, empty, esc, h1, lede, linkRow, page, pending, split,
  type Panel, type PanelContext,
} from '../panel';

/** The class of a card of the list column. While the column holds one
 *  such card, `hub.css` stretches the row, so the card and the community
 *  card beside it are of one height (R2151). */
const LIST = 'newsc';

/** The NEWS panel. */
class NewsPanel implements Panel {
  readonly key = 'news' as const;

  /** The stamp this panel last wrote, so a redraw once a second writes to
   *  the store once a visit and not once a second. */
  private stamped = -1;

  constructor(private ctx: PanelContext) {}

  open(): void {
    // A second visit must clear the dot again, so the guard is dropped
    // here and the next draw of the ready list writes the stamp.
    this.stamped = -1;
  }

  /** The list cell, of `CHROME_NEWS_PAGE` articles. The pane and the dot
   *  read the same one. */
  private news(): Cell<NewsPage> {
    return this.ctx.data.cell<NewsPage>('news', TTL_ON_OPEN, () => getNews(CHROME_NEWS_PAGE));
  }

  dot(): boolean {
    const cell = this.news();
    return cell.value !== undefined && newsDot(cell.value.items, loadSeenAt(NEWS_SEEN_KEY));
  }

  html(): string {
    return page(
      h1(S.title, S.subtitle),
      split(this.ctx.wide(), this.list(), this.community()),
    );
  }

  /** The list, newest first. */
  private list(): string {
    const cell = this.news();
    if (cell.value === undefined) return card(S.card_news_title, '◈', '', pending(cell), LIST);

    const items = [...cell.value.items].sort((a, b) => newsAt(b) - newsAt(a));
    // Opening the section clears the dot. The list may land after `open()`
    // ran, and the shell calls `html` only for the section it shows, so the
    // stamp is written on the first render of a ready list after an open.
    const newest = newestNewsAt(items);
    if (newest !== this.stamped) {
      this.stamped = newest;
      saveSeenAt(NEWS_SEEN_KEY, newest);
    }

    if (items.length === 0) {
      // No press of its own (R1742): the COMMUNITY card beside it holds
      // the one way forward, JOIN THE DISCORD, where the news is told
      // first. A second press would repeat it.
      return card(S.card_news_title, '◈', '',
        empty(S.news_empty, S.news_empty_why), LIST);
    }
    return items.map((n) => this.article(n)).join('');
  }

  /** One article, with its whole text. */
  private article(n: NewsItem): string {
    return card(n.title, '◈', formatAgo(this.ctx.now(), newsAt(n)), this.body(n), LIST);
  }

  /**
   * The body of one article, from the list answer: the list route serves
   * the whole body, so the panel reads no article a second time.
   *
   * The body is Markdown and it goes in as text (R154): `esc` first, and
   * `pre-wrap` so the line breaks the writer typed survive.
   */
  private body(n: NewsItem): string {
    const text = n.body;
    if (text.trim() === '') {
      return empty(S.body_empty, S.body_empty_why);
    }
    return `<div class="lede" style="white-space: pre-wrap">${esc(text)}</div>`;
  }

  /** The community card. One link, and no figure nothing answers. It
   *  wears the one card glyph of the page (R2151). */
  private community(): string {
    return card(S.card_community_title, '◈', '',
      lede(S.community_lede) + linkRow(DISCORD_URL, S.community_join));
  }
}

/** Makes the NEWS panel. */
export function createNewsPanel(ctx: PanelContext): Panel {
  return new NewsPanel(ctx);
}
